import { createAsyncThunk } from '@reduxjs/toolkit';
import { License } from 'common/types/Entities';
import { getConditionsToDisplay } from 'common/utils/AclConditionsHelpers';
import { _getAclEnriesStatusTooltipDates, calculateAclEntriesStatus } from 'common/utils/AclEntriesStatusTooltipHelpers';
import { dateToFrontEndFormat } from 'common/utils/dateHelpers';
import { AclEntriesPage } from 'features/aclEntries/aclEntriesTypeDefs';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';

import { APIAclEntryDelete } from '../Acls/AclEntries.api';
import { APILicensesGet } from '../Licenses/Licenses.api';
import { _formatLicenseResponse } from '../Licenses/LicenseThunkHelpers';

import { Acl, APIUserAclsGetList, UserAcl } from './UserProfileAcls.api';
import { FormattedUserAcl, FormattedUserAclWithLicenseInfo } from './UserProfileAclsThunkTypeDefs';

export const fetchUserProfileAcls = createAsyncThunk(
    'userProfileAcls/fetchUserProfileAcls',
    fetchAndProcessUserProfileAcls
);

export const deleteUserProfileAclEntry = createAsyncThunk(
    'userProfileAcls/deleteUserProfileAclEntries',
    async (item: {
        tenantId: string,
        resourceId?: string,
        accessId?: string,
        userId?: string,
        usergroupId?: string,
        licenseId?: string
    }) => {
        return APIAclEntryDelete(item);
    }
);

function createAclId(acl: Acl) {
    return `${acl.accessId}.${acl.resourceId}.${acl.licenseId}.${acl.usergroupId}.${acl.tenantId}.${acl.userId}.${acl.productPackageId ?? ''}`;
}

function formatAclData(row: UserAcl) : FormattedUserAcl {
    const formattedAcl = {
        ...row,
        ...row.acl,
        startDate: row.acl.startDate ? dateToFrontEndFormat(row.acl.startDate) : '',
        endDate: row.acl.endDate ? dateToFrontEndFormat(row.acl.endDate) : '',
        id: createAclId(row.acl),
        resourceTableName: row.aclResourceDisplayName ?? row.aclResourceName,
        accessTableName: row.aclAccessName,
        conditionsToDisplay: getConditionsToDisplay(row.acl.conditions)
    };
    return formattedAcl;
}

async function fetchAndProcessUserProfileAcls(item: { tenantId: string, userId: string }) {
    const response = await APIUserAclsGetList({
        tenantId: item.tenantId,
        userId: item.userId
    });

    return response.data.map(formatAclData) || [];
}

export const fetchUserProfileAclsAndLicenses = createAsyncThunk(
    'userProfileAcls/fetchUserProfileAclsWithLicenseInfo',
    async (item: { currentUserProfileTenant: IUserProfileTenant, userId: string }): Promise<FormattedUserAclWithLicenseInfo[]> => {
        const { currentUserProfileTenant, userId } = item;
        const { id: tenantId } = currentUserProfileTenant;
        const currentAclPage =  AclEntriesPage.UserProfile;

        const requests = [
            APIUserAclsGetList({ tenantId, userId }),
            APILicensesGet({ tenantId })
        ];

        const responses = await Promise.all(requests);
        const userProfileAcls = (responses[0].data as UserAcl[]).map(formatAclData);
        const licenses = _formatLicenseResponse(responses[1].data as License[]);

        return userProfileAcls.map((userAcl) => {
            const licenseByAclEntry = licenses.find(license => license.id === userAcl.licenseId);

            const licenseBillingRecord = licenseByAclEntry?.billingRecord ?? '';
            const aclEntriesStatusTooltipDates = _getAclEnriesStatusTooltipDates(userAcl, currentUserProfileTenant, licenseByAclEntry);
            const { tooltipProps, status } = calculateAclEntriesStatus(aclEntriesStatusTooltipDates, currentAclPage);

            return {
                ...userAcl,
                licenseBillingRecord,
                status,
                statusTooltip: tooltipProps,
                currentPage: currentAclPage
            };
        });
    }
);
